import type { InvoicePrintSettingsUI } from '@helios/data-access';
import { orgLogoUrl, orgLogoHorizontalUrl } from '../../../shell/shared';
import { getInvoiceSystemTemplate, type InvoiceSystemTemplate } from './system-templates';

/**
 * PRD.238 §7 — Generated Invoice Print Layout.
 *
 * Renders a printable invoice page from:
 *   - the saved Invoice Print settings (template + branding + sections)
 *   - the clinic / provider branding (with app_config defaults applied upstream)
 *   - the patient header data
 *   - the invoice metadata (number, date, status)
 *   - the structured line items + payments
 *
 * Purely presentational. Produces an A4-shaped surface suitable for the
 * wizard's Preview step and for actual print use from the live billing
 * workspace. No database calls here.
 */

// ─── Public types ───

export interface InvoicePrintPatient {
  name: string;
  age?: string;
  sex?: string;
  patientNumber?: string;
}

export interface InvoicePrintMeta {
  invoiceNumber: string;
  invoiceDateIso: string;
  visitDateIso?: string;
  status?: string;
  providerName?: string;
}

export interface InvoicePrintLineItem {
  name: string;
  description?: string;
  qty: number;
  unitPrice: number;
  discount: number;
  taxRate: number;
  amount: number;
}

export interface InvoicePrintPayment {
  amount: number;
  method?: string;
  paidAtIso?: string;
}

export interface InvoicePrintTotals {
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  paymentsTotal: number;
  balanceDue: number;
}

export interface InvoicePrintLayoutProps {
  settings: InvoicePrintSettingsUI;
  patient: InvoicePrintPatient;
  meta: InvoicePrintMeta;
  items: InvoicePrintLineItem[];
  payments: InvoicePrintPayment[];
  totals: InvoicePrintTotals;
  /** Currency formatter — provided by the caller so locale stays consistent. */
  formatMoney: (amount: number) => string;
  accentColor?: string;
  previewMode?: boolean;
}

const DEFAULT_ACCENT = '#5A2A18';

// ─── Helpers ───

function resolveLogoUrl(source: string): string {
  switch (source) {
    case 'standard':
      return orgLogoUrl;
    case 'horizontal':
      return orgLogoHorizontalUrl;
    case 'none':
    default:
      return '';
  }
}

function formatPrintDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Main component ───

export function InvoicePrintLayout({
  settings,
  patient,
  meta,
  items,
  payments,
  totals,
  formatMoney,
  accentColor,
  previewMode,
}: InvoicePrintLayoutProps) {
  const template = getInvoiceSystemTemplate(settings.templateId);
  const accent = accentColor || DEFAULT_ACCENT;
  const logoUrl = resolveLogoUrl(settings.logoSource);
  const compact = template.density === 'compact';
  const pagePadding = compact ? 26 : 36;
  const sectionGap = compact ? 14 : 20;

  return (
    <div
      data-invoice-print="true"
      style={{
        width: '210mm',
        minHeight: '297mm',
        background: '#ffffff',
        color: '#1f2937',
        fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        fontSize: compact ? 11 : 12,
        lineHeight: 1.45,
        padding: pagePadding,
        boxSizing: 'border-box',
        boxShadow: previewMode ? '0 1px 3px rgba(15, 23, 42, 0.12)' : undefined,
        border: previewMode ? '1px solid rgba(15, 23, 42, 0.08)' : undefined,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: sectionGap,
        position: 'relative',
      }}
    >
      <Header
        settings={settings}
        template={template}
        accent={accent}
        logoUrl={logoUrl}
        meta={meta}
      />

      <MetaStrip settings={settings} patient={patient} meta={meta} />

      {template.showInvoiceHeading && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 16,
            fontWeight: 700,
            color: accent,
            letterSpacing: '0.18em',
            paddingTop: 4,
          }}
        >
          INVOICE
        </div>
      )}

      <ItemsTable
        settings={settings}
        template={template}
        items={items}
        accent={accent}
        formatMoney={formatMoney}
      />

      <TotalsBlock
        settings={settings}
        template={template}
        totals={totals}
        payments={payments}
        accent={accent}
        formatMoney={formatMoney}
      />

      <FooterBlock settings={settings} accent={accent} template={template} />
    </div>
  );
}

// ─── Header ───

function Header({
  settings,
  template,
  accent,
  logoUrl,
  meta,
}: {
  settings: InvoicePrintSettingsUI;
  template: InvoiceSystemTemplate;
  accent: string;
  logoUrl: string;
  meta: InvoicePrintMeta;
}) {
  const isHorizontal = settings.logoSource === 'horizontal';
  const logoHeight = template.prominentLogo ? (isHorizontal ? 56 : 64) : 44;

  const clinicTextBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {settings.clinicName && (
        <div
          style={{
            fontSize: template.prominentLogo ? 20 : 16,
            fontWeight: 800,
            letterSpacing: '0.01em',
            color: template.accentStyle === 'plain' ? '#111827' : accent,
            lineHeight: 1.1,
          }}
        >
          {settings.clinicName}
        </div>
      )}
      {settings.clinicAddress && (
        <div style={{ fontSize: 11, color: '#4b5563', whiteSpace: 'pre-line' }}>
          {settings.clinicAddress}
        </div>
      )}
      {(settings.clinicPhone || settings.clinicEmail || settings.clinicWebsite) && (
        <div style={{ fontSize: 11, color: '#4b5563' }}>
          {[settings.clinicPhone, settings.clinicEmail, settings.clinicWebsite]
            .filter(Boolean)
            .join('  ·  ')}
        </div>
      )}
      {settings.taxId && (
        <div style={{ fontSize: 11, color: '#4b5563' }}>Tax ID: {settings.taxId}</div>
      )}
    </div>
  );

  const invoiceMetaBlock = (
    <div
      style={{
        textAlign: 'right',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 160,
      }}
    >
      {settings.showInvoiceNumber && meta.invoiceNumber && (
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
          # {meta.invoiceNumber}
        </div>
      )}
      {settings.showInvoiceDate && (
        <div style={{ fontSize: 11, color: '#4b5563' }}>
          Date: <strong>{formatPrintDate(meta.invoiceDateIso)}</strong>
        </div>
      )}
      {settings.showVisitDate && meta.visitDateIso && (
        <div style={{ fontSize: 11, color: '#4b5563' }}>
          Visit: <strong>{formatPrintDate(meta.visitDateIso)}</strong>
        </div>
      )}
      {meta.status && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#ffffff',
            background: accent,
            padding: '2px 8px',
            borderRadius: 999,
            alignSelf: 'flex-end',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginTop: 2,
          }}
        >
          {meta.status}
        </div>
      )}
    </div>
  );

  if (template.headerAlign === 'center') {
    return (
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          paddingBottom: 12,
          borderBottom:
            template.accentStyle === 'underline' ? `2px solid ${accent}` : '1px solid #d1d5db',
        }}
      >
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Clinic logo"
            style={{
              display: 'block',
              flexShrink: 0,
              objectFit: 'contain',
              height: logoHeight,
              width: 'auto',
              maxWidth: isHorizontal ? 320 : 220,
            }}
          />
        )}
        {clinicTextBlock}
        <div style={{ marginTop: 4 }}>{invoiceMetaBlock}</div>
      </header>
    );
  }

  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        paddingBottom: 12,
        borderBottom:
          template.accentStyle === 'underline' ? `2px solid ${accent}` : '1px solid #d1d5db',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Clinic logo"
            style={{
              display: 'block',
              flexShrink: 0,
              objectFit: 'contain',
              height: logoHeight,
              width: 'auto',
              maxWidth: isHorizontal ? 260 : logoHeight,
            }}
          />
        )}
        {clinicTextBlock}
      </div>
      {invoiceMetaBlock}
    </header>
  );
}

// ─── Patient / billed-to strip ───

function MetaStrip({
  settings,
  patient,
  meta,
}: {
  settings: InvoicePrintSettingsUI;
  patient: InvoicePrintPatient;
  meta: InvoicePrintMeta;
}) {
  if (!settings.showPatientDemographics && !settings.showProvider) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
        padding: '12px 0',
        borderBottom: '1px solid #e5e7eb',
      }}
    >
      {settings.showPatientDemographics && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#6b7280',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 2,
            }}
          >
            Billed to
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
            {patient.name || '—'}
          </div>
          {(patient.age || patient.sex) && (
            <div style={{ fontSize: 11, color: '#4b5563' }}>
              {[patient.age, patient.sex].filter(Boolean).join(' · ')}
            </div>
          )}
          {settings.showPatientNumber && patient.patientNumber && (
            <div style={{ fontSize: 11, color: '#4b5563' }}>Patient #: {patient.patientNumber}</div>
          )}
        </div>
      )}

      {settings.showProvider && meta.providerName && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#6b7280',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 2,
            }}
          >
            Attending Provider
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{meta.providerName}</div>
        </div>
      )}
    </div>
  );
}

// ─── Items table ───

function ItemsTable({
  settings,
  template,
  items,
  accent,
  formatMoney,
}: {
  settings: InvoicePrintSettingsUI;
  template: InvoiceSystemTemplate;
  items: InvoicePrintLineItem[];
  accent: string;
  formatMoney: (amount: number) => string;
}) {
  if (items.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No line items.</div>
    );
  }
  const cellPad = template.density === 'compact' ? '6px 8px' : '8px 10px';
  const filledHeader = template.accentStyle === 'filled';
  const headerCell: React.CSSProperties = {
    padding: cellPad,
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: filledHeader ? '#ffffff' : '#374151',
    background: filledHeader ? accent : 'transparent',
    borderBottom: filledHeader ? 'none' : `2px solid ${accent}`,
    textTransform: filledHeader ? 'none' : 'uppercase',
    letterSpacing: filledHeader ? 'normal' : '0.05em',
  };
  const numHeaderCell: React.CSSProperties = { ...headerCell, textAlign: 'right' };
  const cell: React.CSSProperties = {
    padding: cellPad,
    fontSize: 12,
    color: '#111827',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'top',
  };
  const numCell: React.CSSProperties = { ...cell, textAlign: 'right' };

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: filledHeader ? '1px solid #e5e7eb' : 'none',
        fontSize: 12,
      }}
    >
      <thead>
        <tr>
          <th style={{ ...headerCell, width: 32, textAlign: 'center' }}>#</th>
          <th style={headerCell}>Description</th>
          {settings.showItemQuantity && <th style={{ ...numHeaderCell, width: 60 }}>Qty</th>}
          {settings.showItemUnitPrice && <th style={{ ...numHeaderCell, width: 90 }}>Rate</th>}
          {settings.showItemDiscount && <th style={{ ...numHeaderCell, width: 80 }}>Disc.</th>}
          {settings.showItemTax && <th style={{ ...numHeaderCell, width: 60 }}>Tax %</th>}
          {settings.showItemAmount && <th style={{ ...numHeaderCell, width: 110 }}>Amount</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => (
          <tr key={idx}>
            <td style={{ ...cell, textAlign: 'center', color: '#6b7280' }}>{idx + 1}</td>
            <td style={cell}>
              <div style={{ fontWeight: 600, color: '#111827' }}>{item.name || '—'}</div>
              {item.description && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  {item.description}
                </div>
              )}
            </td>
            {settings.showItemQuantity && <td style={numCell}>{item.qty}</td>}
            {settings.showItemUnitPrice && <td style={numCell}>{formatMoney(item.unitPrice)}</td>}
            {settings.showItemDiscount && (
              <td style={numCell}>{item.discount ? formatMoney(item.discount) : '—'}</td>
            )}
            {settings.showItemTax && (
              <td style={numCell}>{item.taxRate ? `${item.taxRate}%` : '—'}</td>
            )}
            {settings.showItemAmount && (
              <td style={{ ...numCell, fontWeight: 600 }}>{formatMoney(item.amount)}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Totals + payments ───

function TotalsBlock({
  settings,
  template,
  totals,
  payments,
  accent,
  formatMoney,
}: {
  settings: InvoicePrintSettingsUI;
  template: InvoiceSystemTemplate;
  totals: InvoicePrintTotals;
  payments: InvoicePrintPayment[];
  accent: string;
  formatMoney: (amount: number) => string;
}) {
  const rows: Array<{ label: string; value: string; emphasize?: boolean }> = [];
  if (settings.showSubtotal) {
    rows.push({ label: 'Subtotal', value: formatMoney(totals.subtotal) });
  }
  if (settings.showDiscountTotal && totals.discountTotal > 0) {
    rows.push({ label: 'Discount', value: `−${formatMoney(totals.discountTotal)}` });
  }
  if (settings.showTaxTotal && totals.taxTotal > 0) {
    rows.push({ label: 'Tax', value: formatMoney(totals.taxTotal) });
  }
  rows.push({ label: 'Grand Total', value: formatMoney(totals.grandTotal), emphasize: true });

  const fullWidth = template.totalsStyle === 'full_width';
  const minimal = template.totalsStyle === 'minimal';

  const blockStyle: React.CSSProperties = fullWidth
    ? {
        marginTop: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }
    : {
        marginTop: 4,
        marginLeft: 'auto',
        width: minimal ? 220 : 280,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      };

  return (
    <section style={blockStyle}>
      {rows.map((row) => (
        <TotalsRow
          key={row.label}
          label={row.label}
          value={row.value}
          emphasize={row.emphasize}
          accent={accent}
        />
      ))}

      {settings.showPayments && payments.length > 0 && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 8,
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#6b7280',
              marginBottom: 4,
            }}
          >
            Payments
          </div>
          {payments.map((p, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                color: '#374151',
              }}
            >
              <span>
                {formatPrintDate(p.paidAtIso)}
                {p.method ? ` · ${p.method}` : ''}
              </span>
              <span>{formatMoney(p.amount)}</span>
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              fontWeight: 700,
              color: '#111827',
              marginTop: 2,
            }}
          >
            <span>Paid</span>
            <span>{formatMoney(totals.paymentsTotal)}</span>
          </div>
        </div>
      )}

      {settings.showBalanceDue && (
        <div
          style={{
            marginTop: 10,
            padding: '8px 12px',
            borderRadius: 4,
            background: totals.balanceDue > 0 ? `${accent}10` : '#ecfdf5',
            border: `1px solid ${totals.balanceDue > 0 ? accent : '#10b981'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: totals.balanceDue > 0 ? accent : '#065f46',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {totals.balanceDue > 0 ? 'Balance Due' : 'Paid in Full'}
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: totals.balanceDue > 0 ? accent : '#065f46',
            }}
          >
            {formatMoney(totals.balanceDue)}
          </span>
        </div>
      )}
    </section>
  );
}

function TotalsRow({
  label,
  value,
  emphasize,
  accent,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
  accent: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        fontSize: emphasize ? 13 : 12,
        fontWeight: emphasize ? 700 : 500,
        color: emphasize ? accent : '#374151',
        paddingTop: emphasize ? 6 : 0,
        borderTop: emphasize ? '1px solid #e5e7eb' : 'none',
      }}
    >
      <span
        style={{
          textTransform: emphasize ? 'uppercase' : 'none',
          letterSpacing: emphasize ? '0.05em' : 'normal',
        }}
      >
        {label}
      </span>
      <span>{value}</span>
    </div>
  );
}

// ─── Footer ───

function FooterBlock({
  settings,
  accent,
  template,
}: {
  settings: InvoicePrintSettingsUI;
  accent: string;
  template: InvoiceSystemTemplate;
}) {
  if (!settings.showFooterNotes && !settings.showSignatureBlock) return null;
  return (
    <footer
      style={{
        marginTop: 'auto',
        paddingTop: 16,
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 24,
      }}
    >
      <div style={{ fontSize: 11, color: '#4b5563', maxWidth: '60%' }}>
        {settings.showFooterNotes && settings.footerText
          ? settings.footerText
          : settings.showFooterNotes
            ? 'Thank you for your visit.'
            : ''}
      </div>
      {settings.showSignatureBlock && (
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              minWidth: 200,
              borderTop: `1px solid ${template.accentStyle === 'plain' ? '#111827' : accent}`,
              paddingTop: 4,
              fontSize: 11,
              color: '#4b5563',
            }}
          >
            Authorized Signature
          </div>
        </div>
      )}
    </footer>
  );
}
